import * as XLSX from "xlsx";

// Column header -> field path. Anything NOT in this map becomes a
// custom attribute automatically (both on import and export).
export const KNOWN_COLUMNS = {
  Id: "id",
  "Part Type": "partType",
  "Record Type": "recordType",
  Productgroup: "name",
  Manufacturer: "manufacturer",
  "Manufacturer Short Name": "manufacturerShortName",
  Description: "description",
  Type: "type",
  "Article Number": "articleNumber",
  Variant: "variant",
  "External Link": "configuratorUrl", // NEW - configurator / manufacturer page
  Width: "size.width",
  Height: "size.height",
  Depth: "size.depth",
  "Distance Left": "xSpacing.left",
  "Distance Right": "xSpacing.right",
  "Distance Top": "ySpacing.top",
  "Distance Bottom": "ySpacing.bottom",
  "Carrier Rail": "carrierRail",
  "Device Tag": "deviceTag",
  "Cabinet Clearance": "insideClearance",
  "Plate Width": "montageplate.size.width",
  "Plate Height": "montageplate.size.height",
  "Plate Depth": "montageplate.size.depth",
  "Plate Mounting Distance": "montageplate.mountingDistance",
};
const KNOWN_HEADER_SET = new Set(Object.keys(KNOWN_COLUMNS));

// Other names accepted on import (never written on export)
const HEADER_ALIASES = {
  Configurator: "External Link",
  "Configurator Link": "External Link",
  "Configurator URL": "External Link",
};

export const LINK_HEADER = "External Link";
export const LINK_HEADERS = [LINK_HEADER, ...Object.keys(HEADER_ALIASES)];

const canonicalHeader = (header) => HEADER_ALIASES[header] ?? header;

const setPath = (obj, path, value) => {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = cur[parts[i]] ?? {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
};

const getPath = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

// Best-effort type inference for attribute values coming from spreadsheet cells.
export const inferAttributeValue = (raw) => {
  if (raw === undefined || raw === null || raw === "") return "";
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw;

  const str = String(raw).trim();
  if (/^(true|false)$/i.test(str)) return str.toLowerCase() === "true";
  if (/^-?\d+$/.test(str)) return parseInt(str, 10);
  if (/^-?\d+\.\d+$/.test(str)) return parseFloat(str);
  return str;
};

// External link from a cell: empty -> null (removes the link), a bare domain
// ("www.example.com/x") gets "https://". Anything else is passed on as typed;
// the backend (ConfiguratorUrlValidator) rejects values that aren't http(s).
export const normalizeExternalLink = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^[\w-]+(\.[\w-]+)+(:\d+)?([/?#].*)?$/.test(value)) return `https://${value}`;
  return value;
};

// Cells that show text but link somewhere ("Siemens configurator" -> https://...):
// use the hyperlink target instead of the visible text, for the link columns only.
// Call on the sheet before sheet_to_json.
export const applyHyperlinkTargets = (sheet, headers = LINK_HEADERS) => {
  if (!sheet?.["!ref"]) return;
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const wanted = new Set(headers.map((h) => h.trim()));

  for (let c = range.s.c; c <= range.e.c; c++) {
    const head = sheet[XLSX.utils.encode_cell({ r: range.s.r, c })];
    if (!head || !wanted.has(String(head.v ?? "").trim())) continue;

    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      const target = cell?.l?.Target;
      if (target && /^https?:\/\//i.test(target)) {
        cell.t = "s";
        cell.v = target;
        delete cell.w;
      }
    }
  }
};

// row (spreadsheet object) -> article payload
export const rowToArticle = (row, { ownership, tenantId, defaultPartType }) => {
  const article = {
    recordType: "Part",
    partType: defaultPartType,
    ownership,
    tenantId,
    size: {},
    xSpacing: {},
    ySpacing: {},
    montageplate: { size: {} },
    attributes: {},
  };

  Object.entries(row).forEach(([header, value]) => {
    const trimmedHeader = canonicalHeader(header.trim());

    if (KNOWN_HEADER_SET.has(trimmedHeader)) {
      const path = KNOWN_COLUMNS[trimmedHeader];
      setPath(article, path, value);
    } else if (value !== undefined && value !== null && value !== "") {
      article.attributes[trimmedHeader] = inferAttributeValue(value);
    }
  });

  // Only touch the link when the file has the column: no column = keep existing links
  if ("configuratorUrl" in article) {
    article.configuratorUrl = normalizeExternalLink(article.configuratorUrl);
  }

  return article;
};

// articles -> spreadsheet rows, with a union of all attribute keys as extra columns
export const articlesToRows = (articles) => {
  const attributeKeys = new Set();
  articles.forEach((a) => {
    Object.keys(a.attributes ?? {}).forEach((k) => attributeKeys.add(k));
  });

  const knownHeaders = Object.keys(KNOWN_COLUMNS);
  const allHeaders = [...knownHeaders, ...Array.from(attributeKeys)];

  const rows = articles.map((a) => {
    const row = {};
    knownHeaders.forEach((header) => {
      const path = KNOWN_COLUMNS[header];
      row[header] = getPath(a, path) ?? "";
    });
    attributeKeys.forEach((key) => {
      row[key] = a.attributes?.[key] ?? "";
    });
    return row;
  });

  return { headers: allHeaders, rows };
};

export const exportArticlesToExcel = (
  articles,
  fileNamePrefix = "articles",
) => {
  const { headers, rows } = articlesToRows(articles);

  const aoa = [headers, ...rows.map((r) => headers.map((h) => r[h]))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!cols"] = headers.map((h) => ({
    wch: h === LINK_HEADER ? 40 : Math.max(12, String(h).length + 2),
  }));

  // External links as clickable hyperlinks
  const linkCol = headers.indexOf(LINK_HEADER);
  if (linkCol >= 0) {
    rows.forEach((row, i) => {
      const url = row[LINK_HEADER];
      if (typeof url === "string" && /^https?:\/\//i.test(url)) {
        const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: linkCol })];
        if (cell) cell.l = { Target: url, Tooltip: url };
      }
    });
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Articles");

  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${fileNamePrefix}_${date}.xlsx`);
};